import React from 'react';
import PropTypes from 'prop-types';

import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { withViewport } from '../../util/contextHelpers';
import { GoTrophy } from 'react-icons/go';
import { BiAward } from 'react-icons/bi';
import { CiStar } from 'react-icons/ci';

import {
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  Page,
  ExternalLink,
  NamedLink,
  IconReviewStar,
} from '../../components';

import { TopbarContainer } from '..';
import { FormattedMessage } from '../../util/reactIntl';

import css from './VisualCreativeContestPage.css';

import firstPlaceImage from '../../assets/contest2025_first_place_image.jpeg';
import firstPlaceProfileImage from '../../assets/contest2025_first_place_profileImage_small.jpeg';
import secondPlaceImage from '../../assets/contest2025_second_place_image.jpeg';
import secondPlaceProfileImage from '../../assets/contest2025_second_place_profileImage_small.jpeg';
import thirdPlaceImage from '../../assets/contest2025_third_place_image.jpeg';
import thirdPlaceProfileImage from '../../assets/contest2025_third_place_profileImage_small.jpeg';
import fourthPlaceImage from '../../assets/contest2025_fourth_place_image.jpeg';
import fourthPlaceProfileImage from '../../assets/contest2025_fourth_place_profileImage_small.jpeg';
import fifthPlaceImage from '../../assets/contest2025_fifth_place_image.jpeg';
import fifthPlaceProfileImage from '../../assets/contest2025_fifth_place_profileImage_small.jpeg';
import verifiedIcon from '../../assets/verifiedIcon.svg';

const placeImageMap = {
  firstPlace: firstPlaceImage,
  secondPlace: secondPlaceImage,
  thirdPlace: thirdPlaceImage,
  topFinalist: fourthPlaceImage,
  topFinalist2: fifthPlaceImage,
};

const placeProfileImageMap = {
  firstPlace: firstPlaceProfileImage,
  secondPlace: secondPlaceProfileImage,
  thirdPlace: thirdPlaceProfileImage,
  topFinalist: fourthPlaceProfileImage,
  topFinalist2: fifthPlaceProfileImage,
};

const WinnerCard = ({
  place,
  title,
  description,
  authorName,
  authorRole,
  isFinalist,
  id,
  slug,
  userId,
}) => {
  const placeBadgeClass = isFinalist
    ? `${css.placeBadge} ${css.placeBadgeFinalist}`
    : css.placeBadge;
  const placeBadgeTextClass = isFinalist
    ? `${css.placeBadgeText} ${css.placeBadgeTextFinalist}`
    : css.placeBadgeText;

  const workImage = placeImageMap[place];
  const profileImage = placeProfileImageMap[place];

  const renderIcon = () => {
    if (isFinalist) {
      return <CiStar className={css.placeBadgeIcon} />;
    }
    if (place === 'firstPlace') {
      return <GoTrophy className={css.placeBadgeIconFirst} />;
    }
    return <BiAward className={css.placeBadgeIcon} />;
  };

  return (
    <div className={css.winnerCard}>
      <div className={css.winnerImageWrapper}>
        <img src={workImage} alt={title} className={css.winnerImage} />
      </div>
      <div className={css.winnerContent}>
        <div className={css.winnerHeader}>
          <div className={placeBadgeClass}>
            {renderIcon()}
            <span className={placeBadgeTextClass}>
              <FormattedMessage id={`VisualCreativeContestPage.${place}`} />
            </span>
          </div>
          <h2 className={css.winnerTitle}>{title}</h2>
          <p className={css.winnerDescription}>{description}</p>
        </div>
        <div className={css.divider} />
        <div className={css.winnerFooter}>
          <div className={css.authorSection}>
            <div className={css.authorImageWrapper}>
              <img src={verifiedIcon} alt="Verified" className={css.verifiedBadge} />
              <NamedLink name="ProfilePage" params={{ id: userId }}>
                <img src={profileImage} alt={authorName} className={css.authorImage} />
              </NamedLink>
            </div>
            <div className={css.authorInfo}>
              <h3 className={css.authorName}>{authorName}</h3>
              <p className={css.authorRole}>{authorRole}</p>
            </div>
          </div>
          <NamedLink
            className={css.viewButton}
            name="PortfolioWorkPageCanonical"
            params={{ id, slug }}
          >
            <FormattedMessage id="VisualCreativeContestPage.viewPortfolioButton" />
          </NamedLink>
        </div>
      </div>
    </div>
  );
};

export const VisualCreativeContestPageComponent = props => {
  const { intl, scrollingDisabled, viewport } = props;

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org
  // We are using JSON-LD format
  const schemaTitle = intl.formatMessage({ id: 'VisualCreativeContestPage.schemaTitle' });
  const schemaDescription = intl.formatMessage({
    id: 'VisualCreativeContestPage.schemaDescription',
  });

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="VisualCreativeContestPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.content}>
            <div className={css.heroSection}>
              <div className={css.heroBackground}>
                <div className={css.blurEmerald}>
                  <div style={{ width: '100%', height: '100%' }}>
                    <div style={{ height: '300px', width: '100%' }} />
                  </div>
                </div>
                <div className={css.heroContent}>
                  <div className={css.heroBadge}>
                    <div className={css.heroBadgeDot} />
                    <span className={css.heroBadgeText}>
                      <FormattedMessage id="VisualCreativeContestPage.heroBadge" />
                    </span>
                  </div>
                  <div className={css.heroTitleWrapper}>
                    <h1 className={css.heroTitle1}>
                      <FormattedMessage id="VisualCreativeContestPage.heroTitle1" />
                    </h1>
                    <h1 className={css.heroTitle2} style={{ WebkitTextFillColor: 'transparent' }}>
                      <FormattedMessage id="VisualCreativeContestPage.heroTitle2" />
                    </h1>
                  </div>
                  <p className={css.heroDescription}>
                    <FormattedMessage id="VisualCreativeContestPage.heroDescription" />
                  </p>
                </div>
                <div className={css.blurPurple}>
                  <div style={{ width: '100%', height: '100%' }}>
                    <div style={{ width: '100%', height: '100%' }} />
                  </div>
                </div>
                <div className={css.blurCyan} />
              </div>

              <WinnerCard
                place="firstPlace"
                title="NEKALAB | Nekalan kulttuurikeskus: Brändi-identiteetti"
                description="Nekalab on uusi kulttuurikeskus Tampereen Nekalassa. Keskus avautuu 1.1.2026 ja sen tavoitteena on tarjota..."
                authorName="Maarit L"
                authorRole="Brand & UI/UX Designer"
                isFinalist={false}
                id="691488e7-20c6-451b-8d36-8d25b05ce14c"
                slug="nekalab-nekalan-kulttuurikeskus-brandi-identiteetti"
                userId="667d1e08-3676-4947-8c54-fb61c2ba3fdb"
              />

              <WinnerCard
                place="secondPlace"
                title="Zure 2043 -kampanja"
                description="Zure halusi innostaa työntekijänsä uuden sisäisen viestintäalustan käyttöön ja tehdä heistä aktiivisia brändilähettiläitä..."
                authorName="Joona P"
                authorRole="AD / graafinen suunnittelija"
                isFinalist={false}
                id="6914752e-df97-48ed-aef5-c3a15d4defe9"
                slug="zure-2043-kampanja"
                userId="67fd4061-105c-44db-b25e-ec16def2ebac"
              />

              <WinnerCard
                place="thirdPlace"
                title="dussl — Skincare"
                description="dussl is a next-generation skincare brand on a mission to make daily UV protection effortless..."
                authorName="Sanna L"
                authorRole="Graafinen suunnittelija"
                isFinalist={false}
                id="69146065-f6c1-4326-a064-a44d9912416a"
                slug="dussl-skincare"
                userId="641d5e87-9ed0-4144-af62-877b014f2457"
              />

              <WinnerCard
                place="topFinalist"
                title="Designflow 2025 - Kilpailu"
                description="Rinnakkaisuniversumissa on vuosi 2025, ja ihmiskunta on perustanut siirtokuntia Kuuhun lähes vuosikymmenen..."
                authorName="Vuong B"
                authorRole="Brand Designer"
                isFinalist={true}
                id="691db168-dd73-4e8b-bb0b-8a5546a1343a"
                slug="designflow-2025-kilpailu"
                userId="68dbccac-cbb5-4825-ba65-860885695eef"
              />

              <WinnerCard
                place="topFinalist2"
                title="Pikku Kakkosen Eskari"
                description="Pikku Kakkosen Eskari -sovellus on suunniteltu esiopetusikäisille lapsille yhteistyössä eri alojen ammattilaisten ja..."
                authorName="Sanna K"
                authorRole="Graafinen suunnittelija"
                isFinalist={true}
                id="691de74a-1ddd-45fb-a6fd-a4c4c2c406ee"
                slug="pikku-kakkosen-eskari"
                userId="6863b7b2-24a9-4d99-afc7-aa1565739bd5"
              />

              <div className={css.aboutSection}>
                <h2 className={css.aboutTitle}>
                  <FormattedMessage id="VisualCreativeContestPage.aboutTitle" />
                </h2>
                <p className={css.aboutDescription}>
                  <FormattedMessage
                    id="VisualCreativeContestPage.aboutDescription"
                    values={{ lineBreak: <br /> }}
                  />
                </p>
              </div>
            </div>

            <div className={css.joinSection}>
              <p className={css.joinText1}>
                <FormattedMessage id="VisualCreativeContestPage.joinCommunityText1" />
              </p>
              <NamedLink className={css.joinText2} name="SignupPage">
                <FormattedMessage id="VisualCreativeContestPage.joinCommunityText2" />
              </NamedLink>
            </div>

            <div className={css.footerCtaSection}>
              <h2 className={css.footerCtaTitle}>
                <FormattedMessage id="VisualCreativeContestPage.footerCtaTitle" />
              </h2>
              <p className={css.footerCtaSubtitle}>
                <FormattedMessage id="VisualCreativeContestPage.footerCtaSubtitle" />
              </p>
              <NamedLink className={css.footerCtaButton} name="SignupPage">
                <FormattedMessage id="VisualCreativeContestPage.footerCtaButton" />
              </NamedLink>
            </div>
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

VisualCreativeContestPageComponent.defaultProps = {
  scrollingDisabled: false,
};

const { number, shape } = PropTypes;

VisualCreativeContestPageComponent.propTypes = {
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
  };
};

const VisualCreativeContestPage = compose(
  withRouter,
  connect(mapStateToProps),
  withViewport,
  injectIntl
)(VisualCreativeContestPageComponent);

export default VisualCreativeContestPage;
